import {Injectable, Optional} from '@angular/core';
import {Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {MsalBroadcastService, MsalService} from '@azure/msal-angular';
import {InteractionStatus} from '@azure/msal-browser';
import {from, of} from 'rxjs';
import {catchError, filter, map, switchMap, take} from 'rxjs/operators';
import {environment} from '../../../environments/environment';
import {AuthenticationService} from '../services/authentication.service';

/**
 * Guard class for redirecting the user to the login page, if he is not logged in.
 * ATM this is done by verifying if a jwt token exists in the local storage.
 */
@Injectable()
export class AuthGuard implements CanActivate {

    constructor(
        private router: Router,
        @Optional() private msalService: MsalService,
        @Optional() private msalBroadcastService: MsalBroadcastService,
        private authService: AuthenticationService
    ) {
    }

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): any {
        if (!environment.msalEnable) {
            if (localStorage.getItem('user_token')) {
                return true;
            }
            this.router.navigate(['/auth'], {queryParams: {returnUrl: state.url}});
            return false;
        }

        // Check if legacy auth is required (user doesn't have SSO access)
        if (this.authService.isLegacyAuth()) {
            if (localStorage.getItem('user_token')) {
                return true;
            }
            this.router.navigate(['/auth/login'], {queryParams: {returnUrl: state.url, legacyAuth: true}});
            return false;
        }

        if (!this.msalService || !this.msalBroadcastService) {
            this.router.navigate(['/auth'], {queryParams: {returnUrl: state.url}});
            return false;
        }

        return this.handleMsalAuth(state);
    }

    private handleMsalAuth(state: RouterStateSnapshot) {
        const scopes = this.authService.getMsalScopes();

        // If we already have a valid token, allow access without triggering MSAL
        // This handles the case where a technical user session is restored
        if (localStorage.getItem('user_token')) {
            return of(true);
        }

        // Wait for any in-progress MSAL interaction to complete
        return this.msalBroadcastService.inProgress$.pipe(
            filter((status: InteractionStatus) => status === InteractionStatus.None),
            take(1),
            switchMap(() => {
                this.ensureActiveAccount();

                const activeAccount = this.msalService.instance.getActiveAccount();
                if (!activeAccount) {
                    this.triggerMsalLogin(state.url, scopes, true);
                    return of(false);
                }

                if (localStorage.getItem('user_token')) {
                    return of(true);
                }

                return this.acquireTokenAndExchange(activeAccount, state.url, scopes);
            })
        );
    }

    private ensureActiveAccount(): void {
        const accounts = this.msalService.instance.getAllAccounts();
        if (!this.msalService.instance.getActiveAccount() && accounts.length) {
            this.msalService.instance.setActiveAccount(accounts[0]);
        }
    }

    private triggerMsalLogin(returnUrl: string, scopes: string[], forceAccountSelection = false): void {
        const request: any = {
            scopes,
            redirectStartPage: returnUrl,
        };

        if (forceAccountSelection) {
            request.prompt = 'select_account';
        }

        this.msalService.loginRedirect(request);

    }

    private acquireTokenAndExchange(activeAccount: any, returnUrl: string, scopes: string[]) {
        const request = {account: activeAccount, scopes};

        return from(this.msalService.instance.acquireTokenSilent(request)).pipe(
            catchError(() => {
                this.triggerMsalLogin(returnUrl, scopes);
                return of(null);
            }),
            switchMap(result => {
                if (!result) {
                    return of(false);
                }
                return this.authService.exchangeMsalTokenForJwt(result.accessToken).pipe(
                    map(() => true),
                    catchError((apiError) => this.handleExchangeError(apiError, returnUrl, scopes))
                );
            })
        );
    }

    private handleExchangeError(error: any, returnUrl: string, scopes: string[]) {
        const errorStatus = error?.status;
        const errorMessage = error?.error?.message || error?.message;

        if (errorStatus === 401 && errorMessage === 'errorMessage.401.user.noOneIdmAccess') {
            this.authService.setLegacyAuthRequired(true);
            localStorage.removeItem('user_token');
            this.router.navigate(['/auth/login'], {queryParams: {returnUrl, legacyAuth: true}});
            return of(false);
        }

        this.triggerMsalLogin(returnUrl, scopes);
        return of(false);
    }
}

